import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import clsx from "clsx";
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";

// Size variants - component dışında sabit obje
const sizeClasses = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-6 w-6",
};

const Checkbox = forwardRef(
  (
    {
      children,
      className = "",
      labelClassName = "",
      inputClassName = "",
      description,
      disabled = false,
      checked,
      size = "md", // "sm", "md", "lg"
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslationDetector();

    // Internal ref for our own use
    const internalRef = useRef(null);

    // Forward the ref to both React Hook Form and our internal use
    useImperativeHandle(ref, () => internalRef.current);

    // Memoized class names
    const checkboxClassName = useCallback(
      () =>
        clsx(
          "rounded border-2 transition-all duration-200 ease-in-out focus:ring-transparent focus:outline-none",
          sizeClasses[size],
          // Normal state
          "border-border text-primary focus:ring-transparent focus:outline-none",
          // Checked state - primary renk
          "checked:border-primary checked:bg-primary",
          // Hover state
          "hover:border-primary/60 hover:shadow-sm",
          // Disabled state
          disabled && "hover:border-border cursor-not-allowed opacity-50",
          // Custom input classes
          inputClassName,
        ),
      [size, disabled, inputClassName],
    );

    const labelWrapperClassName = useCallback(
      () =>
        clsx(
          "flex cursor-pointer items-start gap-3 transition-colors duration-200",
          disabled && "cursor-not-allowed",
          className,
        ),
      [disabled, className],
    );

    const textClassName = useCallback(
      () =>
        clsx(
          "transition-colors duration-200 select-none",
          size === "sm" ? "text-sm" : size === "lg" ? "text-base" : "text-sm",
          disabled ? "text-foreground-muted" : "text-foreground",
          labelClassName,
        ),
      [size, disabled, labelClassName],
    );

    const descriptionClassName = useCallback(
      () =>
        clsx(
          "mt-1 text-xs transition-colors duration-200",
          disabled ? "text-foreground-muted/70" : "text-foreground-muted",
        ),
      [disabled],
    );

    return (
      <label className={labelWrapperClassName()}>
        <input
          type="checkbox"
          ref={internalRef}
          disabled={disabled}
          checked={checked}
          className={checkboxClassName()}
          {...props}
        />

        <div className="flex-1">
          <div className={textClassName()}>{children}</div>
          {description && (
            <div className={descriptionClassName()}>{description}</div>
          )}
        </div>
      </label>
    );
  },
);

Checkbox.displayName = "Checkbox";

// Memo ile props karşılaştırması
const MemoizedCheckbox = memo(Checkbox);
MemoizedCheckbox.displayName = "Checkbox";

export default MemoizedCheckbox;
